import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useSite } from "../lib/SiteContext";
import { mediaUrl } from "../lib/api";

const links = [
  { href: "#charters", label: "Charters" },
  { href: "#fleet", label: "The Boat" },
  { href: "#destinations", label: "Destinations" },
  { href: "#faq", label: "FAQ" },
  { href: "#contact", label: "Contact" },
];

const Navigation = () => {
  const { content } = useSite();
  const brand = content?.site?.brand_name || "Islands Escape";
  const suffix = content?.site?.brand_suffix || "Malta Charters";
  const logo = content?.site?.logo_url;
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const onHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goTo = (href) => {
    setOpen(false);
    if (!onHome) {
      window.location.href = "/" + href;
      return;
    }
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "glass-nav shadow-sm" : "bg-transparent"
      }`}
      data-testid="site-nav"
    >
      <div className="container-x flex items-center justify-between py-4 md:py-5">
        <Link to="/" className="flex items-center gap-3 leading-none" data-testid="brand-link">
          {logo && (
            <img
              src={mediaUrl(logo)}
              alt={brand}
              className="h-10 md:h-12 w-auto object-contain"
              onError={(e) => (e.currentTarget.style.display = "none")}
            />
          )}
          <div className="flex flex-col leading-none">
            <span className="font-serif text-2xl md:text-3xl tracking-tight text-primary">{brand}</span>
            <span className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground mt-1">{suffix}</span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <button
              key={l.href}
              onClick={() => goTo(l.href)}
              className="text-sm tracking-wide text-primary/80 hover:text-primary transition-colors relative group"
              data-testid={`nav-link-${l.label.toLowerCase()}`}
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 w-0 h-px bg-secondary transition-all group-hover:w-full" />
            </button>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <Link to="/book" className="btn-primary text-sm" data-testid="nav-book-cta">
            Book your charter
          </Link>
        </div>

        <button
          className="lg:hidden p-2 text-primary"
          onClick={() => setOpen(!open)}
          data-testid="nav-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden glass-nav border-t border-white/40" data-testid="mobile-menu">
          <div className="container-x py-6 flex flex-col gap-4">
            {links.map((l) => (
              <button
                key={l.href}
                onClick={() => goTo(l.href)}
                className="text-left text-base text-primary py-2"
                data-testid={`mobile-link-${l.label.toLowerCase()}`}
              >
                {l.label}
              </button>
            ))}
            <Link to="/book" className="btn-primary text-sm mt-2" onClick={() => setOpen(false)}>
              Book your charter
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navigation;
